"use client";
import { ReactNode, useEffect } from "react";
import * as React from "react";
import { AssistantRuntimeProvider, ChatModelAdapter, useLocalRuntime } from "@assistant-ui/react";
import { getToken } from "@/utils/localstorage";
import { useAlphaAgentStore } from "@/state/alphaAgentStore";
import { useEffectAsync } from "@alphascriptsolutions/client-core";
import { Spin, Typography } from "antd";
import axios from "axios";
import { getSelectedFeatures, getSelectedFeatureTradingData } from "@/utils/alphaAgent/modelDataHelper";

const apiUrl = process.env.API_URL;
const INIT_SENTINEL_FEATURE = "__INIT_FEATURE_DETAILS__";
// Stores the form ID we are currently attributing messages to.
// This keeps messages "sticky" to the originating form until the user types for a new form.
const ACTIVE_SAVING_FORM_KEY = "alphaActiveSavingForm";

function sanitizeThreadMessages(messages: any[]): any[] {
  const arr = Array.isArray(messages) ? messages : [];
  return arr
    .filter((m) => m && typeof m === "object")
    .filter((m) => m.role === "user" || m.role === "assistant" || m.role === "system")
    .map((m) => ({
      ...m,
      content: Array.isArray(m.content)
        ? m.content
            .filter((p: any) => p && typeof p === "object" && p.type === "text" && typeof p.text === "string")
            .filter((p: any) => p.text !== INIT_SENTINEL_FEATURE)
        : [],
    }))
    .filter((m) => m.content.length > 0);
}

// Helper function to parse text and image tags into segments
export function parseTextAndImages(text: string): string[] {
  const segments: string[] = [];
  const imgTagRegex = /<img[^>]*\/?>/g;
  let lastIndex = 0;
  let match;

  while ((match = imgTagRegex.exec(text)) !== null) {
    // Add text before the image tag
    if (match.index > lastIndex) {
      const textBefore = text.slice(lastIndex, match.index);
      // Split text into individual characters
      for (let i = 0; i < textBefore.length; i++) {
        segments.push(textBefore[i]);
      }
    }

    // Add the image tag as a single segment
    segments.push(match[0]);
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last image tag
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    for (let i = 0; i < remainingText.length; i++) {
      segments.push(remainingText[i]);
    }
  }

  return segments;
}

export const MyModelAdapter: ChatModelAdapter = {
  async *run({ messages }): AsyncGenerator {
    const authToken = await getToken();
    // Extract latest user input (if any) from the thread
    const lastMessage = messages[messages.length - 1];
    let userInput = "";

    if (lastMessage?.content && Array.isArray(lastMessage.content)) {
      const textPart = lastMessage.content.find(
        (part) => typeof part === "object" && "text" in part && typeof part.text === "string"
      );
      if (textPart) userInput = (textPart as any).text;
    }
    // INIT sentinel indicates the UI is asking the model to start the feature step
    const isInit = userInput === INIT_SENTINEL_FEATURE;

    // Build request payload (INIT vs normal user input)
    let requestBody: any = { userInput };
    if (isInit) {
      const { customFeatureModel, updateQuestionsForStep, setSelectedFeature } = useAlphaAgentStore.getState();
      const tradingVolume = getSelectedFeatureTradingData(customFeatureModel);
      localStorage.setItem("tradingVolume", JSON.stringify(tradingVolume));
      const selectedFeatures = getSelectedFeatures(customFeatureModel);
      const noOfFeatures = selectedFeatures.length === 0 ? 1 : selectedFeatures.length;
      updateQuestionsForStep(2, noOfFeatures);
      selectedFeatures.forEach((featureKey) => {
        if (featureKey) setSelectedFeature(featureKey, false);
      });
      requestBody = { userInput: { selectedFeatures } };
    }

    // Signal run start before the network request to cover backend latency
    try {
      window.dispatchEvent(new CustomEvent("ai-run-start"));
    } catch (e) {}

    // Call backend and get response + updated global state
    const result = await axios.post(`${apiUrl}/alpha-create/interact`, requestBody, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    });

    localStorage.setItem("alphaCreateData", JSON.stringify(result.data.data.globalState));
    const {
      markAnswered,
      getSelectedFeature,
      setSelectedFeature,
      setDisableBackButton,
      setSelectedFeatureMessagesData,
    } = useAlphaAgentStore.getState();

    const maybePrev = messages[messages.length - 2];
    const maybeLast = messages[messages.length - 1];
    // Convert thread messages to the lightweight shape we persist in store
    const toThreadLike = (m: any) => {
      if (!m || (m.role !== "user" && m.role !== "assistant" && m.role !== "system")) return undefined;
      const content = Array.isArray(m.content)
        ? m.content
            .filter((p: any) => p && p.type === "text" && typeof p.text === "string")
            .filter((p: any) => p.text !== INIT_SENTINEL_FEATURE)
        : [];
      if (content.length === 0) return undefined;
      return { role: m.role, content };
    };
    const prevLike = toThreadLike(maybePrev);
    const lastLike = toThreadLike(maybeLast);
    // Form ID the server reports as the current active form for this turn
    const serverCurrentForm: string = result.data.data.currentForm;

    // Compact, form-sticky routing with duplicate prevention
    // Read the active form (where we currently attribute messages)
    const readActive = (): string | undefined => {
      try {
        const raw = localStorage.getItem(ACTIVE_SAVING_FORM_KEY);
        return raw ? JSON.parse(raw) : undefined;
      } catch {
        return undefined;
      }
    };
    // Persist the active form
    const writeActive = (formId?: string) => {
      try {
        if (formId) localStorage.setItem(ACTIVE_SAVING_FORM_KEY, JSON.stringify(formId));
      } catch {}
    };
    // Get messages array for a given form from the store
    const getMsgs = (formId: string) =>
      (useAlphaAgentStore.getState().selectedFeatureMessagesData?.[formId] ?? []) as any[];
    // Shallow-serialize comparison to prevent duplicate pushes
    const equals = (a: any, b: any) => {
      try {
        return JSON.stringify(a) === JSON.stringify(b);
      } catch {
        return false;
      }
    };
    // Append message only if it differs from last saved one
    const pushIfNew = (formId: string, msg: any) => {
      const arr = getMsgs(formId);
      const last = arr.length > 0 ? arr[arr.length - 1] : undefined;
      if (!last || !equals(last, msg)) setSelectedFeatureMessagesData(formId, msg);
    };

    // Routing rule:
    // - INIT turn: start saving under serverCurrentForm and remember it
    // - Assistant-only turn: keep saving under previously active form
    // - User turn: save under previously active form, then switch active to serverCurrentForm for next turn
    const userJustTyped = maybeLast?.role === "user" && !isInit;
    let targetFormForSave = readActive() || serverCurrentForm;
    if (isInit) {
      targetFormForSave = serverCurrentForm;
      writeActive(serverCurrentForm);
    }

    if (prevLike) pushIfNew(targetFormForSave, prevLike);
    if (lastLike) pushIfNew(targetFormForSave, lastLike);
    if (userJustTyped) writeActive(serverCurrentForm);

    const step = 2;
    const globalState = result.data.data.globalState;
    if (result.data.data.step === 4) {
      setDisableBackButton(true);
      markAnswered(2);
    }

    const featureFormMap: Record<string, string> = {
      "percentage-change-chart": "percentageChangeChartForm",
      "constant-value-line": "constantValueLineForm",
      "custom-built-indices": "customBuiltIndicesForm",
      "vertical-line": "verticalLineForm",
      "background-shading": "backgroundShadingForm",
      "call-out-boxes": "callOutBoxesForm",
      "summary-table": "summaryTableForm",
      "small-line-chart": "smallLineChartForm",
    };
    Object.entries(featureFormMap).forEach(([featureKey, formKey]) => {
      if (globalState[formKey] && getSelectedFeature(featureKey) !== true) {
        markAnswered(step);
        setSelectedFeature(featureKey, true);
      }
    });

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    // Only include intro if this is the first assistant response in the thread
    const hasAssistantInThread = Array.isArray(messages) && messages.some((m: any) => m?.role === "assistant");
    const includeIntro = isInit && !hasAssistantInThread && result.data.data.step !== 4;
    const fullText = includeIntro
      ? `Hey! In this step we are now going to work through each of the **Custom Features** you selected previously.` +
        `Working through one Custom Feature at a time, we will discuss the key considerations in configuring each of the Custom Features you requested.\n\n` +
        String(result.data.data.response ?? "")
      : String(result.data.data.response ?? "");

    const segments = parseTextAndImages(fullText);
    try {
      window.dispatchEvent(new CustomEvent("ai-run-start"));
    } catch (e) {}

    for (let i = 0; i < segments.length; i++) {
      const currentSegments = segments.slice(0, i);
      const currentText = currentSegments.join("");
      if (i === 0 && segments.length > 0) {
        try {
          window.dispatchEvent(new CustomEvent("ai-first-token"));
        } catch (e) {}
      }
      yield { content: [{ type: "text", text: currentText }] };
      await delay(8);
    }
    try {
      window.dispatchEvent(new CustomEvent("ai-run-end"));
    } catch (e) {}
  },
};

export function MyRuntimeProviderFeatureDetails({ children }: { children: ReactNode }) {
  const [loading, setLoading] = React.useState(true);
  const [previousMessages, setPreviousMessages] = React.useState<any[]>([]);
  const { currentStep } = useAlphaAgentStore();

  useEffectAsync(async () => {
    if (currentStep === 2) {
      // Restore any previously saved feature messages from store
      const { selectedFeatureMessagesData } = useAlphaAgentStore.getState();
      // Read latest completion state to filter only completed forms
      let latestGlobalState: any = undefined;
      try {
        const stored = localStorage.getItem("alphaCreateData");
        latestGlobalState = stored ? JSON.parse(stored) : undefined;
      } catch (e) {}
      const formNames = [
        "percentageChangeChartForm",
        "constantValueLineForm",
        "verticalLineForm",
        "backgroundShadingForm",
        "callOutBoxesForm",
        "summaryTableForm",
        "smallLineChartForm",
        "customBuiltIndicesForm",
      ];
      const allMessages: any[] = [];
      formNames.forEach((formName) => {
        const isCompleted = Boolean(latestGlobalState?.[formName]);
        if (!isCompleted) return;
        const msgs = selectedFeatureMessagesData[formName];
        if (Array.isArray(msgs)) {
          allMessages.push(...msgs);
        }
      });

      const sanitizedMessages = sanitizeThreadMessages(allMessages);

      // Clear first, then set sanitized messages to avoid UI duplication artifacts
      setPreviousMessages([]);
      setPreviousMessages(sanitizedMessages);
      setLoading(false);
    }
  }, [currentStep]);

  useEffect(() => {
    // Ensure the scroll happens after the component has updated and loading is false
    if (previousMessages.length === 0) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, []);

  if (loading)
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <Spin size="large" tip="Loading" />
        <Typography.Text>Loading</Typography.Text>
      </div>
    );

  return (
    <MyRuntimeProviderReady
      key={`${currentStep}-${previousMessages.length}`}
      previousMessages={previousMessages}
      currentStep={currentStep}
    >
      {children}
    </MyRuntimeProviderReady>
  );
}

function MyRuntimeProviderReady({
  children,
  previousMessages,
  currentStep,
}: {
  children: ReactNode;
  previousMessages: any;
  currentStep: number;
}) {
  const runtime = useLocalRuntime(MyModelAdapter, {
    initialMessages: sanitizeThreadMessages(previousMessages),
  });

  // Always request the latest backend response upon entering step 2
  React.useEffect(() => {
    const thread = (runtime as any)?.thread;
    if (!thread || typeof thread.append !== "function") return;
    if (currentStep !== 2) return;
    thread.append({ role: "system", content: [{ type: "text", text: INIT_SENTINEL_FEATURE }], startRun: true });
  }, [runtime, currentStep]);

  return (
    <AssistantRuntimeProvider key={`${currentStep}-${previousMessages?.length ?? 0}`} runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}
